<template>
  <el-dialog
    title="新增库存调整"
    :visible.sync="dialogVisible"
    width="980px"
    custom-class="manual-inventory-adjust-dialog"
    :close-on-click-modal="false"
    append-to-body
    @close="handleClose"
  >
    <div class="add-row">
      <el-select
        class="add-product-type"
        size="small"
        filterable
        clearable
        placeholder="产品类型"
        v-model="addForm.productType"
        @change="onProductTypeChange"
      >
        <el-option
          v-for="product in productList"
          :key="product.type || product.productType"
          :label="product.productName"
          :value="product.productType"
        ></el-option>
      </el-select>
      <el-select
        class="add-sku"
        size="small"
        filterable
        clearable
        placeholder="选择 SKU"
        v-model="addForm.sku"
        :disabled="!addForm.productType"
        :loading="skuLoading"
        @keyup.enter.native="onAddLine"
      >
        <el-option
          v-for="item in skuOptions"
          :key="item.sku"
          :label="item.label"
          :value="item.sku"
        ></el-option>
      </el-select>
      <el-input
        class="add-qty"
        size="small"
        type="number"
        placeholder="调整数量（≠0）"
        v-model="addForm.adjustQty"
        @keyup.enter.native="onAddLine"
      ></el-input>
      <el-button type="primary" size="small" :disabled="skuLoading" @click="onAddLine">添加</el-button>
    </div>
    <div class="add-tip">支持添加多个 SKU；调整数量 &gt; 0 盘盈，&lt; 0 盘亏；等于 0 不可添加/提交。</div>

    <el-table
      :data="lines"
      border
      stripe
      size="mini"
      max-height="420"
      show-summary
      :summary-method="getSummaries"
    >
      <el-table-column label="序号" width="50" align="center">
        <template slot-scope="scope">{{ scope.$index + 1 }}</template>
      </el-table-column>
      <el-table-column prop="shopName" label="店铺" width="70" align="center" show-overflow-tooltip></el-table-column>
      <el-table-column prop="productName" label="产品名称" min-width="110" show-overflow-tooltip></el-table-column>
      <el-table-column prop="purchaseColorName" label="采购颜色" width="90" align="center" show-overflow-tooltip></el-table-column>
      <el-table-column prop="sku" label="SKU" min-width="180" show-overflow-tooltip></el-table-column>
      <el-table-column prop="model" label="机型" width="90" align="center" show-overflow-tooltip></el-table-column>
      <el-table-column prop="adjustQty" label="调整数量" width="120" align="center">
        <template slot-scope="scope">
          <el-input
            type="number"
            size="mini"
            v-model="scope.row.adjustQty"
            @input="onLineQtyInput(scope.row)"
            @blur="onLineQtyBlur(scope.row)"
          ></el-input>
        </template>
      </el-table-column>
      <el-table-column label="方向" width="70" align="center">
        <template slot-scope="scope">
          <span :class="{ 'qty-gain': scope.row.adjustQty > 0, 'qty-loss': scope.row.adjustQty < 0 }">
            {{ formatDirection(scope.row.adjustQty) }}
          </span>
        </template>
      </el-table-column>
      <el-table-column label="操作" width="70" align="center">
        <template slot-scope="scope">
          <el-button type="text" size="mini" @click="removeLine(scope.$index)">删除</el-button>
        </template>
      </el-table-column>
    </el-table>

    <div class="line-summary" v-if="lines.length">
      <span>共 {{ lines.length }} 个 SKU</span>
      <span>盘盈 {{ gainCount }} / 盘亏 {{ lossCount }}</span>
      <span>合计调整数量 {{ totalAdjustQty }}</span>
    </div>

    <div class="remark-row">
      <span class="remark-label">备注</span>
      <el-input
        type="textarea"
        :rows="2"
        size="small"
        maxlength="500"
        show-word-limit
        v-model="remark"
        placeholder="请输入备注"
      ></el-input>
    </div>

    <div slot="footer" class="dialog-footer">
      <el-button size="small" @click="dialogVisible = false">取 消</el-button>
      <el-button type="primary" size="small" :loading="submitting" @click="onSubmit">提 交</el-button>
    </div>
  </el-dialog>
</template>

<script>
import request from "../../request/requestModules.js";
import { activeProducts, allActiveProducts } from "../../utils/constant.js";

function createClientRequestId() {
  return `manual-adj-${Date.now()}-${Math.random()
    .toString(36)
    .slice(2, 10)}`;
}

function parseAdjustQty(raw) {
  if (raw === "" || raw == null) return { error: "请填写调整数量" };
  const text = String(raw).trim();
  if (!text) return { error: "请填写调整数量" };
  if (text.includes(".")) return { error: "调整数量必须为整数" };
  const qtyNum = Number(text);
  if (!Number.isFinite(qtyNum) || !Number.isInteger(qtyNum)) {
    return { error: "调整数量必须为整数" };
  }
  if (qtyNum === 0) return { error: "调整数量不能为 0" };
  return { qty: qtyNum };
}

export default {
  name: "CreateManualInventoryAdjustDialog",
  data() {
    return {
      dialogVisible: false,
      productList: localStorage.getItem("shopType") == 6 ? allActiveProducts : activeProducts,
      addForm: {
        productType: "",
        sku: "",
        adjustQty: "",
      },
      skuOptions: [],
      skuLoading: false,
      lines: [],
      remark: "",
      submitting: false,
      clientRequestId: "",
    };
  },
  computed: {
    totalAdjustQty() {
      return (this.lines || []).reduce((sum, row) => sum + (Number(row.adjustQty) || 0), 0);
    },
    gainCount() {
      return (this.lines || []).filter(row => Number(row.adjustQty) > 0).length;
    },
    lossCount() {
      return (this.lines || []).filter(row => Number(row.adjustQty) < 0).length;
    },
  },
  methods: {
    open() {
      this.dialogVisible = true;
      this.resetForm();
      this.clientRequestId = createClientRequestId();
    },
    resetForm() {
      this.addForm = { productType: "", sku: "", adjustQty: "" };
      this.skuOptions = [];
      this.lines = [];
      this.remark = "";
      this.submitting = false;
    },
    handleClose() {
      this.resetForm();
      this.clientRequestId = "";
    },
    formatDirection(qty) {
      const n = Number(qty);
      if (!Number.isFinite(n) || n === 0) return "-";
      return n > 0 ? "盘盈" : "盘亏";
    },
    onProductTypeChange() {
      this.addForm.sku = "";
      this.skuOptions = [];
      if (this.addForm.productType === "" || this.addForm.productType == null) return;
      this.skuLoading = true;
      request
        .fetchProducts({
          productType: this.addForm.productType,
          beginIndex: 0,
          pageSize: 10000,
        })
        .then(res => {
          const list = res.ldata || [];
          this.skuOptions = list
            .filter(item => item && String(item.status || "") === "Active")
            .map(item => {
              const sku = String(item["seller-sku"] || item.sku || "").trim();
              const color = item.purchaseColorName || item.productColorName || "";
              const model = item.model || "";
              return {
                sku,
                label: [sku, color, model].filter(Boolean).join(" / "),
                product: item,
              };
            })
            .filter(item => item.sku)
            .sort((a, b) => (a.sku > b.sku ? 1 : -1));
        })
        .catch(err => {
          this.$message.error((err && err.message) || "加载 SKU 失败");
        })
        .finally(() => {
          this.skuLoading = false;
        });
    },
    onAddLine() {
      const sku = String(this.addForm.sku || "").trim();
      if (!sku) {
        this.$message.warning("请选择 SKU");
        return;
      }
      if (this.lines.some(line => line.sku === sku)) {
        this.$message.warning(`SKU「${sku}」已添加`);
        return;
      }
      const parsed = parseAdjustQty(this.addForm.adjustQty);
      if (parsed.error) {
        this.$message.warning(parsed.error);
        return;
      }
      const option = this.skuOptions.find(item => item.sku === sku);
      const product = (option && option.product) || {};
      this.lines.push({
        sku,
        shopName: product.shopName || "",
        productName: product.productName || "",
        purchaseColorName: product.purchaseColorName || "",
        model: product.model || "",
        adjustQty: parsed.qty,
      });
      this.addForm.sku = "";
      this.addForm.adjustQty = "";
    },
    onLineQtyInput(row) {
      if (row.adjustQty === "" || row.adjustQty == null) return;
      const text = String(row.adjustQty);
      if (text.includes(".")) {
        row.adjustQty = String(parseInt(text, 10));
      }
    },
    onLineQtyBlur(row) {
      const parsed = parseAdjustQty(row.adjustQty);
      if (parsed.error) {
        this.$message.warning(parsed.error);
        return;
      }
      row.adjustQty = parsed.qty;
    },
    removeLine(index) {
      this.lines.splice(index, 1);
    },
    validateLines() {
      if (!this.lines.length) {
        this.$message.warning("请至少添加一个 SKU");
        return false;
      }
      const seen = new Set();
      for (let i = 0; i < this.lines.length; i++) {
        const row = this.lines[i];
        const sku = String(row.sku || "").trim();
        if (!sku) {
          this.$message.warning(`第 ${i + 1} 行 SKU 不能为空`);
          return false;
        }
        if (seen.has(sku)) {
          this.$message.warning(`SKU「${sku}」重复，请合并后提交`);
          return false;
        }
        seen.add(sku);
        const parsed = parseAdjustQty(row.adjustQty);
        if (parsed.error) {
          this.$message.warning(`SKU「${sku}」${parsed.error}`);
          return false;
        }
        row.adjustQty = parsed.qty;
      }
      return true;
    },
    getSummaries(param) {
      const { columns, data } = param;
      const sums = [];
      columns.forEach((column, index) => {
        if (index === 0) {
          sums[index] = "合计";
          return;
        }
        if (column.property === "adjustQty") {
          sums[index] = (data || []).reduce((s, row) => s + (Number(row.adjustQty) || 0), 0);
          return;
        }
        sums[index] = "";
      });
      return sums;
    },
    onSubmit() {
      if (this.submitting) return;
      if (!this.validateLines()) return;
      if (!this.clientRequestId) {
        this.clientRequestId = createClientRequestId();
      }
      this.submitting = true;
      request
        .createManualInventoryAdjust({
          lines: this.lines.map(line => ({
            sku: line.sku,
            adjustQty: Number(line.adjustQty),
          })),
          remark: this.remark,
          clientRequestId: this.clientRequestId,
        })
        .then(res => {
          if (res.result != 1 && res.code != 0) {
            this.$message.error(res.message || "新增库存调整失败");
            return;
          }
          const planId = (res.mdata && res.mdata.planId) || "";
          this.$message.success(planId ? `新增成功，调整单号 ${planId}` : "新增成功");
          this.dialogVisible = false;
          this.$emit("success", res.mdata || {});
        })
        .catch(err => {
          this.$message.error((err && err.message) || "新增库存调整失败");
        })
        .finally(() => {
          this.submitting = false;
        });
    },
  },
};
</script>

<style lang="scss">
.manual-inventory-adjust-dialog {
  .add-row {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
  }
  .add-product-type {
    width: 180px;
  }
  .add-sku {
    flex: 1;
    min-width: 240px;
  }
  .add-qty {
    width: 140px;
  }
  .add-tip {
    color: #909399;
    font-size: 12px;
    margin-bottom: 12px;
  }
  .line-summary {
    margin-top: 8px;
    font-size: 13px;
    color: #606266;
    span {
      margin-right: 16px;
    }
  }
  .remark-row {
    display: flex;
    align-items: flex-start;
    margin-top: 12px;
  }
  .remark-label {
    width: 48px;
    line-height: 32px;
    color: #606266;
  }
  .qty-gain {
    color: #67c23a;
  }
  .qty-loss {
    color: #f56c6c;
  }
}
</style>
